'use client';

import {
	Close as DialogClose,
	Content as DialogContent,
	Description as DialogDescription,
	Overlay as DialogOverlay,
	Portal as DialogPortal,
	Root as DialogRoot,
	Title as DialogTitle,
} from '@radix-ui/react-dialog';
import type { Product } from '@repo/shopify';
import { X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCart } from '@/components/cart/cart-provider';
import { VariantSelector } from '@/components/product/variant-selector';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { localizePathForCurrentMarket } from '@/lib/markets';
import { formatMoney } from '@/lib/money';

/** A subscription gift whose product needs variant selection before adding. */
export interface GiftSelection {
	productHandle: string;
	title: string;
}

interface GiftVariantModalProps {
	/** Gifts to select, one sheet step per gift. Empty closes the modal. */
	gifts: GiftSelection[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

async function fetchGiftProduct(handle: string): Promise<Product | null> {
	// fresh=1 bypasses data caches so sold-out options reflect live inventory.
	const response = await fetch(
		localizePathForCurrentMarket(
			`/api/products?handles=${encodeURIComponent(handle)}&fresh=1&imageCount=1`,
		),
		{ cache: 'no-store' },
	);
	if (!response.ok) return null;
	const data = (await response.json()) as { products?: Product[] };
	return data.products?.[0] ?? null;
}

/**
 * Bottom sheet for picking a multi-variant subscription gift's options (e.g.
 * a free tee's size) right after the subscription lands in the cart — the same
 * flow as the unlocked-reward claim sheet, minus the multi-step count. The
 * chosen variant is added as a `_free_gift` line over the open cart drawer.
 */
// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: gift sheet combines live product fetch, option selection, and sequential-gift state
export function GiftVariantModal({ gifts, open, onOpenChange }: GiftVariantModalProps) {
	const { addItem } = useCart();
	const [giftIndex, setGiftIndex] = useState(0);
	const [product, setProduct] = useState<Product | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});

	const gift = gifts[giftIndex];

	useEffect(() => {
		if (!open) {
			setGiftIndex(0);
			setProduct(null);
			setSelectedOptions({});
		}
	}, [open]);

	useEffect(() => {
		if (!open || !gift) return;
		let cancelled = false;

		setIsLoading(true);
		setProduct(null);
		void fetchGiftProduct(gift.productHandle)
			.then((next) => {
				if (cancelled) return;
				setProduct(next);
				const firstAvailable =
					next?.variants?.edges?.map((edge) => edge.node).find((v) => v.availableForSale) ??
					next?.variants?.edges?.[0]?.node;
				const defaults: Record<string, string> = {};
				for (const option of firstAvailable?.selectedOptions ?? []) {
					defaults[option.name] = option.value;
				}
				setSelectedOptions(defaults);
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [open, gift]);

	const variants = useMemo(
		() => product?.variants?.edges?.map((edge) => edge.node) ?? [],
		[product?.variants],
	);

	const selectedVariant = useMemo(
		() =>
			variants.find((variant) =>
				variant.selectedOptions.every((option) => selectedOptions[option.name] === option.value),
			),
		[selectedOptions, variants],
	);

	const handleOptionChange = useCallback((name: string, value: string) => {
		setSelectedOptions((prev) => ({ ...prev, [name]: value }));
	}, []);

	const advance = useCallback(() => {
		if (giftIndex >= gifts.length - 1) {
			onOpenChange(false);
		} else {
			setGiftIndex((prev) => prev + 1);
		}
	}, [giftIndex, gifts.length, onOpenChange]);

	const handleAddGift = useCallback(async () => {
		if (!product || !selectedVariant?.availableForSale || isAdding) return;

		setIsAdding(true);
		try {
			const attributes = [{ key: '_free_gift', value: 'true' }];
			await addItem(
				selectedVariant.id,
				1,
				{
					productId: product.id,
					title: product.title,
					handle: product.handle,
					vendor: product.vendor,
					productType: product.productType,
					image: selectedVariant.image || product.featuredImage,
					variantTitle:
						selectedVariant.title === 'Default Title' ? undefined : selectedVariant.title,
					sku: selectedVariant.sku,
					price: selectedVariant.price,
					attributes,
				},
				{ analyticsSource: 'subscription_gift', openCart: false },
			);
			advance();
		} finally {
			setIsAdding(false);
		}
	}, [addItem, advance, isAdding, product, selectedVariant]);

	if (!gift) return null;

	const image = product?.featuredImage ?? null;
	const price = selectedVariant?.price || product?.priceRange?.minVariantPrice;
	const isAvailable = selectedVariant?.availableForSale ?? false;

	return (
		<DialogRoot open={open} onOpenChange={onOpenChange}>
			<DialogPortal>
				<DialogOverlay className="fixed inset-0 z-[70] bg-black/40" />
				<DialogContent className="fixed inset-x-0 bottom-0 z-[70] mx-auto flex max-h-[90dvh] max-w-lg flex-col overflow-hidden rounded-t-lg bg-white shadow-2xl">
					<DialogDescription className="sr-only">
						Choose options for your free {gift.title}.
					</DialogDescription>
					<DialogClose className="absolute right-3 top-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white/80 text-neutral-600 shadow-sm backdrop-blur-sm transition-colors hover:bg-white hover:text-neutral-900">
						<X className="h-4 w-4" />
						<span className="sr-only">Skip gift</span>
					</DialogClose>

					<div className="flex items-start gap-3 px-4 pb-2 pt-4">
						<div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded border border-neutral-200 bg-neutral-50">
							{image && (
								<ShopifyImage
									src={image.url}
									alt={image.altText || gift.title}
									fill
									className="object-cover"
									sizes="80px"
								/>
							)}
						</div>
						<div className="min-w-0 flex-1 pr-8">
							<p className="text-[11px] font-bold uppercase tracking-wider text-brand-accent">
								Free gift with your subscription
								{gifts.length > 1 ? ` · ${giftIndex + 1} of ${gifts.length}` : ''}
							</p>
							<DialogTitle className="mt-0.5 truncate text-sm font-bold uppercase tracking-wide text-neutral-900">
								{product?.title ?? gift.title}
							</DialogTitle>
							{price && (
								<div className="mt-0.5 flex items-center gap-2">
									<span className="text-xs text-neutral-400 line-through">
										{formatMoney(price)}
									</span>
									<span className="text-xs font-bold text-brand-accent">FREE</span>
								</div>
							)}
						</div>
					</div>

					<div className="flex-1 overflow-y-auto px-4 pb-2">
						{isLoading ? (
							<div className="flex h-28 items-center justify-center">
								<div className="h-7 w-7 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900" />
							</div>
						) : product ? (
							<VariantSelector
								options={product.options || []}
								selectedOptions={selectedOptions}
								variants={variants}
								onOptionChange={handleOptionChange}
							/>
						) : (
							<p className="py-6 text-center text-sm text-neutral-500">
								This gift is unavailable right now.
							</p>
						)}
						{!isLoading && product && selectedVariant && !isAvailable && (
							<p className="mt-2 text-sm text-neutral-500">This option is sold out.</p>
						)}
					</div>

					<div className="flex shrink-0 gap-2 px-4 pb-4 pt-2">
						<button
							type="button"
							className="rounded border border-neutral-300 px-4 py-3 text-xs font-bold uppercase tracking-wider text-neutral-900 transition-colors hover:border-neutral-900"
							onClick={advance}
						>
							Skip
						</button>
						<button
							type="button"
							className={`flex-1 rounded py-3 text-xs font-bold uppercase tracking-wider transition-all ${
								product && isAvailable && !isAdding
									? 'bg-brand-accent text-white hover:brightness-110'
									: 'bg-neutral-200 text-neutral-400'
							}`}
							onClick={handleAddGift}
							disabled={!product || !isAvailable || isAdding}
						>
							{isAdding ? 'Adding…' : !product || isAvailable ? 'Add Free Gift' : 'Sold Out'}
						</button>
					</div>
				</DialogContent>
			</DialogPortal>
		</DialogRoot>
	);
}
